/**
 * B000222 ゾーンコード照会
 */

import { Column, Query } from "@material-table/core";
import { B000222Find, B000222FindRequest, catchRestError } from "clients/rest";
import {
  msgState,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  zoneCd: string;
  kana: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyo: number;
  sokoCd: string;
  zoneCd: string;
  zoneNm: string;
  zoneKana: string;
  floor: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  zoneCd: "",
  kana: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "3rem", align: "right" },
  { field: "sokoCd", hidden: true },
  { field: "zoneCd", title: "ゾーン", width: "6rem" },
  { field: "zoneNm", title: "ゾーン名", width: "6rem" },
  { field: "zoneKana", hidden: true },
  { field: "floor", hidden: true },
];

const focusId = "B000222";

export const B000222 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  useEffect(() => {
    focus(1);
  });

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000222FindRequest = {
        zoneCd: fields.zoneCd,
        kana: fields.kana,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        // ["出力項目名,順番(asc/desc", ...]
        sort: ["zoneCd,asc"],
      };
      // API呼び出し
      B000222Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="ゾーンコード照会"
        gridTemplateColumns="6rem 5rem 6rem 2rem "
      >
        <SLabel>ゾーン</SLabel>
        <SNumberField
          label="ゾーンコード"
          name="zoneCd"
          code
          size={2}
          {...register(1)}
        />
        <SLabel gridColumn="1">ゾーン名カナ</SLabel>
        <STextField
          lable="ゾーン名カナ"
          name="kana"
          size={40}
          {...register(2)}
          gridColumn="span 2"
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="3"
          gridColumn="4"
          {...register(3)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
